import { useEffect, useRef } from 'react';
import { usePrevious } from 'hooks/usePrevious';
import { useKeyboardOpenState } from 'hooks/useKeyboardOpenState';
import { useKeyboardJustClosed } from 'hooks/useKeyboardJustClosed';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';

/**
 * Re-snaps the incident detail drawer to its default once the keyboard hides,
 * fixing incidents opened from search against the keyboard-shrunk viewport.
 */
export const useRestoreEntityDrawerAfterSearchSelection = (): void => {
  const isKeyboardOpen = useKeyboardOpenState();
  const hasKeyboardClosed = useKeyboardJustClosed();
  const selectedGeoEventId = useSelectedGeoEventId();
  const prevSelectedGeoEventId = usePrevious(selectedGeoEventId);
  const { restoreEntityDrawer } = useMapEntityDrawer();
  const pendingRestoreRef = useRef(false);

  // A new incident selected while the keyboard is up opens against the shrunk viewport.
  useEffect(() => {
    if (
      selectedGeoEventId &&
      selectedGeoEventId !== prevSelectedGeoEventId &&
      isKeyboardOpen
    ) {
      pendingRestoreRef.current = true;
    }
  }, [selectedGeoEventId, prevSelectedGeoEventId, isKeyboardOpen]);

  // Keyboard hidden, viewport settled: re-assert the default snap.
  useEffect(() => {
    if (hasKeyboardClosed && pendingRestoreRef.current) {
      pendingRestoreRef.current = false;
      restoreEntityDrawer();
    }
  }, [hasKeyboardClosed, restoreEntityDrawer]);
};
